import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";
import { useAuth } from "./auth.jsx";

const BrandContext = createContext(null);

export const BrandProvider = ({ children }) => {
  const { isAdmin, user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(() => localStorage.getItem("ah_brand") || null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/brands");
      setBrands(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("ah_token");
    if (!token || !user) {
      setBrands([]);
      setLoading(false);
      return;
    }
    load();
  }, [load, user]);

  useEffect(() => {
    if (!brands.length) return;
    const nextBrandId = activeBrandId || (isAdmin ? "all" : brands[0]?.id);
    const isAllowedAll = isAdmin && nextBrandId === "all";
    const exists = brands.some((brand) => brand.id === nextBrandId);
    if (!activeBrandId && nextBrandId) {
      setActiveBrandId(nextBrandId);
      localStorage.setItem("ah_brand", nextBrandId);
      return;
    }
    if (!isAllowedAll && !exists) {
      const fallback = isAdmin ? "all" : brands[0]?.id;
      setActiveBrandId(fallback);
      if (fallback) localStorage.setItem("ah_brand", fallback);
    }
  }, [brands, activeBrandId, isAdmin]);

  const setActive = (id) => {
    const nextId = id === "all" && !isAdmin ? brands[0]?.id : id;
    setActiveBrandId(nextId);
    if (nextId) localStorage.setItem("ah_brand", nextId);
  };

  const canSelectAll = isAdmin;
  const brandQuery = activeBrandId && activeBrandId !== "all" ? { brand_id: activeBrandId } : {};
  const activeBrand = brands.find((b) => b.id === activeBrandId);

  return (
    <BrandContext.Provider value={{ brands, activeBrandId, setActive, brandQuery, activeBrand, reload: load, loading, canSelectAll }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
